import { lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Layout from "./components/Layout";
import PersistentLogin from "./components/PersistentLogin";
import RequireAdmin from "./components/RequireAdmin";
import RequireAuth from "./components/RequireAuth";
import RequireGuest from "./components/RequireGuest";
import useAuth from "./hooks/useAuth";
import { Navigate } from "react-router-dom";
import { AuthProvider } from "./context/Auth";
import { MyCartProvider } from "./context/MyCart";
import NotFound from "./errors/NotFound";
import { SectionProvider } from "./context/Section";
import { TrackingProvider } from "./context/Tracking";

const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Addresses = lazy(() => import("./pages/Dashboard/Addresses"));
const CreateAddress = lazy(() => import("./pages/Dashboard/Addresses/create"));
const EditAddress = lazy(() => import("./pages/Dashboard/Addresses/edit"));
const Company = lazy(() => import("./pages/Dashboard/Company"));
const Orders = lazy(() => import("./pages/Dashboard/Orders"));
const SearchProductsCode = lazy(() => import("./pages/SearchProductsCode"));
const SearchProductsByCode = lazy(() => import("./pages/SearchProductsByCode"));
const SearchProductsByBrandModel = lazy(() =>
  import("./pages/SearchProductsByBrandModel")
);
const ViewAcceptedOrder = lazy(() =>
  import("./pages/Dashboard/Orders/view-accepted")
);
const ViewPendingOrder = lazy(() =>
  import("./pages/Dashboard/Orders/view-pending")
);
const Quotations = lazy(() => import("./pages/Dashboard/Quotations"));
const EditQuotation = lazy(() => import("./pages/Dashboard/Quotations/edit"));
const Settings = lazy(() => import("./pages/Dashboard/Settings"));
const Users = lazy(() => import("./pages/Dashboard/Users"));
const EditUser = lazy(() => import("./pages/Dashboard/Users/edit"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const OrderCreated = lazy(() => import("./pages/OrderCreated"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Register = lazy(() => import("./pages/Register"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Search = lazy(() => import("./pages/Search"));
const ProductsCategory = lazy(() => import("./pages/ProductsCategory"));
const ProductsItems = lazy(() => import("./pages/ProductsItems"));
const MyMachines = lazy(() => import("./pages/Dashboard/MyMachines"));
const MyEngines = lazy(() => import("./pages/Dashboard/MyEngines"));

function App() {
  return (
    <AuthProvider>
      <MyCartProvider>
        <TrackingProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<PersistentLogin />}>
                <Route element={<Layout />}>
                  <Route element={<RequireGuest redirectUrl="/" />}>
                    <Route path="login" element={<Login />} />

                    <Route path="register" element={<Register />} />

                    <Route
                      path="forgot-password"
                      element={<ForgotPassword />}
                    />

                    <Route path="reset-password" element={<ResetPassword />} />
                  </Route>

                  <Route element={<RequireAuth redirectUrl="/login" />}>
                    <Route path="cart" element={<Cart />} />
                    <Route path="search" element={<Search />} />

                    <Route index element={<Home />} />

                    {/* <Route
                    path="searchByCode"
                    element={<SearchProductsByCode />}
                  /> */}
                    <Route
                      path="searchByBrandModel"
                      element={<SearchProductsByBrandModel />}
                    />

                    <Route
                      path="searchByCode"
                      element={<SearchProductsCode />}
                    />

                    <Route
                      path="ProductsCategory/:category/sections/:modelId"
                      element={
                        <SectionProvider>
                          <ProductsCategory />
                        </SectionProvider>
                      }
                    />
                    <Route
                      path="ProductsItems/:category/items/:sectionId/:modelId"
                      element={
                        <SectionProvider>
                          <ProductsItems />
                        </SectionProvider>
                      }
                    />

                    <Route path="checkout" element={<Checkout />} />
                    <Route path="order-created" element={<OrderCreated />} />

                    <Route path="dashboard">
                      <Route index element={<Dashboard />} />

                      <Route path="orders">
                        <Route index element={<Orders />} />
                        <Route
                          path="a/:orderId"
                          element={<ViewAcceptedOrder />}
                        />
                        <Route
                          path="p/:orderId"
                          element={<ViewPendingOrder />}
                        />
                      </Route>

                      <Route path="quotations">
                        <Route index element={<Quotations />} />
                        <Route
                          path=":quotationId"
                          element={<EditQuotation />}
                        />
                      </Route>

                      <Route path="company" element={<Company />} />
                      <Route path="MyMachines" element={<MyMachines />} />
                      <Route path="MyEngines" element={<MyEngines />} />

                      <Route path="addresses">
                        <Route index element={<Addresses />} />
                        <Route path="create" element={<CreateAddress />} />
                        <Route path=":Codice/:Prog" element={<EditAddress />} />
                      </Route>

                      <Route element={<RequireAdmin />}>
                        <Route path="users">
                          <Route index element={<Users />} />
                          <Route path=":customerId" element={<EditUser />} />
                        </Route>
                      </Route>

                      <Route path="settings" element={<Settings />} />
                    </Route>
                  </Route>

                  <Route path="privacy" element={<PrivacyPolicy />} />
                </Route>
              </Route>

              <Route
                path="*"
                element={
                  <div className="flex items-center justify-center min-h-screen">
                    <NotFound />
                  </div>
                }
              />
            </Routes>
          </BrowserRouter>
        </TrackingProvider>
      </MyCartProvider>
    </AuthProvider>
  );
}

export default App;
